import { useMemo } from 'react';
import { useGetUserSubscription, SubscriptionItem } from '../../api/subscriptions';
import { useAppContext } from '../../contexts/Storage';

export interface MappedSubscriptionData {
  currentPlan: string;
  planDisplayName: string;
  subscriptionStatus: string;
  subscriptionType: string;
  price: number;
  startDate: string | null;
  isActive: boolean;
}

const useSubscription = () => {
  const { user } = useAppContext();

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetUserSubscription({
    userId: user?.id,
    filters: {
      subscription_status: 'active',
    },
  });

  const activeSubscription = useMemo(() => data?.activeSubscription || null, [data]);

  const mappedData = useMemo((): MappedSubscriptionData | null => {
    if (!activeSubscription) {
      return {
        currentPlan: 'free',
        planDisplayName: 'Free Plan',
        subscriptionStatus: 'inactive',
        subscriptionType: 'monthly',
        price: 0,
        startDate: null,
        isActive: false,
      };
    }

    const formatPlanName = (name: string): string => {
      const planNames: Record<string, string> = {
        'free_trial': 'Free Trial',
        'free': 'Free Plan',
        'pro': 'Pro Plan',
        'business': 'Business Plan',
        'enterprise': 'Enterprise Plan',
      };
      return planNames[name.toLowerCase()] || name.charAt(0).toUpperCase() + name.slice(1);
    };

    return {
      currentPlan: activeSubscription.subscription_name,
      planDisplayName: formatPlanName(activeSubscription.subscription_name),
      subscriptionStatus: activeSubscription.subscription_status,
      subscriptionType: activeSubscription.subscription_type,
      price: activeSubscription.subscription_price,
      startDate: activeSubscription.created_at,
      isActive: activeSubscription.subscription_status === 'active',
    };
  }, [activeSubscription]);

  const allSubscriptions = useMemo(() => data?.subscriptions || [], [data]);

  return {
    activeSubscription,
    allSubscriptions,
    mappedData,
    isLoading,
    error,
    refetch,
  };
};

export default useSubscription;
